/*eslint-disable*/
import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    SelectChangeEvent,
    Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar"; // Import DateRangePicker
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { frFR } from "@mui/x-data-grid/locales";

import "dayjs/locale/fr"; // Import French locale for dayjs

dayjs.locale("fr");

import FastAPIClient from "../../client";
import config from "../../config";
import { DataGridPro, GridColDef, GridRowsProp } from "@mui/x-data-grid-pro"; // Import DataGridPro

const client = new FastAPIClient(config);

dayjs.extend(isBetween);

type UserActivity = {
    user_id: string;
    first_name: string;
    last_name: string;
    activity_date: string;
    event_count: number;
};

const UserLoginTable: React.FC = () => {
    const [data, setData] = useState<UserActivity[] | null>(null);
    const [selectedUser, setSelectedUser] = useState<string>("all");
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("week"),
        dayjs().endOf("week"),
    ]);
    const [timePeriod, setTimePeriod] = useState<string>("currentWeek");
    const [openDialog, setOpenDialog] = useState<boolean>(false);

    useEffect(() => {
        fetchActivity();
    }, []);

    const fetchActivity = async () => {
        try {
            const response = await client.fetchUserActivity();
            setData(response.results);
            console.log(response.results);
        } catch (error) {
            console.error("Error fetching activity:", error);
        }
    };

    useEffect(() => {
        switch (timePeriod) {
            case "lastMonth":
                setDateRange([
                    dayjs().subtract(1, "month").startOf("month"),
                    dayjs().subtract(1, "month").endOf("month"),
                ]);
                break;
            case "custom":
                break;
            default:
                setDateRange([dayjs().startOf("week"), dayjs().endOf("week")]);
                break;
        }
    }, [timePeriod]);

    const handleTimePeriodChange = (event: SelectChangeEvent<string>) => {
        setTimePeriod(event.target.value);
        if (event.target.value === "custom") {
            setOpenDialog(true);
        }
    };

    const handleDateRangeChange = (newValue: [Dayjs | null, Dayjs | null]) => {
        setDateRange(newValue);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const uniqueUsers = useMemo(() => {
        if (!data) return [];
        const userMap = new Map<string, UserActivity>();

        data.forEach((user) => {
            const fullName = `${user.first_name} ${user.last_name}`;
            if (!userMap.has(fullName)) {
                userMap.set(fullName, user);
            }
        });

        return Array.from(userMap.values());
    }, [data]);

    const filteredData = useMemo(() => {
        if (!data || !dateRange[0] || !dateRange[1]) return [];

        const [start, end] = dateRange;

        // Filter data to only include the desired date range
        const filtered = data;

        // Create a map to aggregate the login counts by user
        const aggregateMap = new Map<string, number>();
        const lastConnectionMap = new Map<string, string>(); // Map to store the last connection date for each user

        // Aggregate activity for each user, counting only days where event_count > 0
        filtered.forEach((entry) => {
            const userId = entry.user_id;
            const eventCount = entry.event_count;
            const activityDate = dayjs(entry.activity_date).format(
                "YYYY-MM-DD"
            ); // Format to get a date string

            // Track the most recent activity date where event_count > 0 (last connection)
            if (eventCount > 0) {
                if (
                    !lastConnectionMap.has(userId) ||
                    dayjs(lastConnectionMap.get(userId)).isBefore(activityDate)
                ) {
                    lastConnectionMap.set(userId, activityDate); // Update with the latest date if event_count > 0
                }

                // Increment login count for the user
                if (!aggregateMap.has(userId)) {
                    aggregateMap.set(userId, 0);
                }
                aggregateMap.set(userId, aggregateMap.get(userId)! + 1);
            }
        });

        // Get all users, including those without any activity (e.g., from the `data` source)
        const allUsers = new Set(data.map((entry) => entry.user_id));

        // Ensure every user is in the aggregateMap with a login count of 0 if they have no activity
        allUsers.forEach((userId) => {
            if (!aggregateMap.has(userId)) {
                aggregateMap.set(userId, 0); // Set their count to 0 if no activity
            }
            if (!lastConnectionMap.has(userId)) {
                lastConnectionMap.set(userId, "-"); // Set last connection as "-" if no activity with event_count > 0
            }
        });

        // Map the aggregateMap into the final structure
        const finalResults = Array.from(aggregateMap.entries()).map(
            ([userId, loginCount]) => {
                const user = data.find((u) => u.user_id === userId);
                return {
                    id: userId, // Unique `id` for each row
                    userId,
                    fullName: user
                        ? `${user.first_name} ${user.last_name}`
                        : "Unknown User",
                    loginCount, // Number of days with event_count > 0 or 0 if no activity
                    lastConnection: lastConnectionMap.get(userId), // Add last connection column
                };
            }
        );

        // Sort the results by `loginCount` in descending order
        return finalResults.sort((a, b) => b.loginCount - a.loginCount);
    }, [data, dateRange]);

    const columns: GridColDef[] = [
        {
            field: "avatar",
            headerName: "",
            width: 70, // Use flex instead of width to evenly distribute
            cellClassName: "centerCell",
            renderCell: (params) => {
                const avatarUrl = params.row.avatar_url || ""; // Use avatar URL or a default empty value
                return (
                    <Avatar
                        alt={params.row.fullName}
                        src={avatarUrl}
                        sx={{ width: 20, height: 20 }}
                    />
                );
            },
        },
        {
            field: "fullName",
            headerName: "Utilisateur",
            flex: 1, // Use flex instead of width to evenly distribute
            cellClassName: "centerCell",
        },
        {
            field: "loginCount",
            cellClassName: "centerCell",
            headerName: "Nombre de connexion",
            flex: 1, // Use flex instead of width to evenly distribute
            // type: "number",
        },
        {
            field: "lastConnection",
            cellClassName: "centerCell",
            headerName: "Dernière connexion",
            flex: 1, // Use flex instead of width to evenly distribute
            renderCell: (params) => {
                const lastConnection = params.row.lastConnection;
                if (lastConnection === "-") {
                    return lastConnection; // If no activity, return "-"
                } else {
                    // Format the last connection date using dayjs in French
                    const formattedDate =
                        dayjs(lastConnection).format("D MMMM YYYY");

                    // Capitalize the first letter of the month
                    const [day, month, year] = formattedDate.split(" ");
                    const capitalizedMonth =
                        month.charAt(0).toUpperCase() + month.slice(1);
                    return `${day} ${capitalizedMonth} ${year}`;
                }
            },
        },
    ];

    return (
        <Card elevation={0}>
            <CardContent>
                {!data ? (
                    <CircularProgress />
                ) : (
                    <>
                        {/* <Grid
                            container
                            spacing={2}
                            mb={2}
                            justifyContent={"flex-end"}
                        >
                            <FormControl fullWidth>
                                <Select
                                    value={timePeriod}
                                    onChange={handleTimePeriodChange}
                                    sx={{
                                        p: 0.5, // overall padding (optional, for outer spacing)
                                        "& .MuiSelect-select": {
                                            padding: "4px 8px", // minimal internal padding
                                        },
                                        fontSize: "0.75rem", // smaller text globally
                                    }}
                                >
                                    <MenuItem value="currentWeek">
                                        Semaine en cours
                                    </MenuItem>
                                    <MenuItem value="lastMonth">
                                        Mois dernier
                                    </MenuItem>
                                    <MenuItem value="custom">
                                        Plage personnalisée
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid> */}

                        {/* Custom Date Range Dialog */}
                        <Dialog open={openDialog} onClose={handleDialogClose}>
                            <DialogTitle>Choose Date Range</DialogTitle>
                            <DialogContent>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DateRangeCalendar
                                        value={dateRange}
                                        onChange={handleDateRangeChange}
                                    />
                                </LocalizationProvider>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleDialogClose}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDialogClose}
                                    color="primary"
                                >
                                    Ok
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ height: 300, width: "100%" }}>
                                <DataGridPro
                                    rows={filteredData}
                                    columns={columns}
                                    getRowClassName={(params) =>
                                        params.row.loginCount === 0
                                            ? "login-warning"
                                            : ""
                                    }
                                    rowHeight={36} // 🔽 Reduce from default 52
                                    hideFooter // Hide footer
                                    sx={{
                                        "& .MuiDataGrid-columnHeaders": {
                                            fontWeight: "bold !important",
                                            fontSize: "0.9rem",
                                        },
                                        "& .MuiDataGrid-root": {
                                            border: "none",
                                        },
                                        "& .MuiDataGrid-footer": {
                                            display: "none",
                                        },
                                        "& .MuiDataGrid-row": {
                                            borderBottom: "none", // 🔴 Removes borders between rows
                                            borderTop: "none", // 🔴 Removes borders between rows
                                        },
                                        "& .MuiDataGrid-cell": {
                                            borderBottom: "none", // Remove bottom border from cells
                                            borderTop: "none", // Remove bottom border from cells
                                        },
                                        "& .centerCell": {
                                            display: "flex",
                                            alignItems: "center",
                                            // justifyContent: "center",
                                        },
                                        "& .MuiDataGrid-columnHeader": {
                                            borderRight: "none", // removes divider between header columns
                                        },
                                        "& .MuiDataGrid-columnSeparator": {
                                            display: "none", // hides the draggable column separator
                                        },
                                        "& .login-warning": {
                                            backgroundImage: `
        repeating-linear-gradient(
            135deg,
rgb(253, 241, 243),
            rgb(253, 241, 243) 4px,
rgb(255, 218, 222) 4px,
            rgb(255, 218, 222) 5px
        )
    `,
                                            backgroundSize: "auto",
                                            backgroundRepeat: "repeat",
                                        },
                                    }}
                                    localeText={
                                        frFR.components.MuiDataGrid.defaultProps
                                            .localeText
                                    }
                                />
                            </Box>
                        </LocalizationProvider>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default UserLoginTable;
